import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadApi } from '../services/api';
import { Lead } from '../types';
import { StageChip } from './WorkBits';

/** ⌘K — find a lead by name or phone from any screen. Rides the same
 *  `GET /leads?search=` the leads table uses; no separate search index. */
const CommandPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Lead[]>([]);
  const [sel, setSel] = useState(0);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else { setQ(''); setHits([]); setSel(0); }
  }, [open]);

  // Debounced so typing a 10-digit phone is one request, not ten.
  useEffect(() => {
    if (!open || q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      const res = await leadApi.list(1, 8, { search: q.trim() });
      setHits(res.data);
      setSel(0);
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;

  const go = (lead: Lead) => { navigate(`/leads/${lead.id}`); onClose(); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return onClose();
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && hits[sel]) go(hits[sel]);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/30 flex items-start justify-center pt-24"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-[540px] max-w-[92%] bg-warm-white border-[1.5px] border-sketch rounded-xl shadow-sketch-lg overflow-hidden">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search leads by name or phone…"
          className="w-full border-0 border-b-[1.5px] border-sketch px-4 py-3.5 text-[15px]
                     bg-transparent text-charcoal placeholder-graphite/60 focus:outline-none"
        />
        <div className="p-2 max-h-80 overflow-y-auto">
          {q.trim().length < 2 && (
            <p className="text-graphite text-[12.5px] px-3 py-3">Type at least two characters.</p>
          )}
          {q.trim().length >= 2 && hits.length === 0 && (
            <p className="text-graphite text-[12.5px] px-3 py-3">No lead matches “{q.trim()}”.</p>
          )}
          {hits.map((lead, i) => (
            <div
              key={lead.id}
              role="button"
              tabIndex={-1}
              onMouseEnter={() => setSel(i)}
              onClick={() => go(lead)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] cursor-pointer
                          ${i === sel ? 'bg-sage/20' : ''}`}
            >
              <span className="avat w-7 h-7 text-[11px]">{lead.name.charAt(0).toUpperCase()}</span>
              <span className="truncate font-medium">{lead.name}</span>
              <span className="num ml-auto text-[11.5px] text-graphite">{lead.mobile}</span>
              <StageChip stage={lead.stage} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
